import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, TrendingUp, Gift, Activity, Percent, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PERIODS = [
    { id: 'today', label: 'Today' },
    { id: 'week',  label: 'This Week' },
    { id: 'month', label: 'This Month' },
    { id: 'year',  label: 'This Year' },
    { id: 'all',   label: 'All Time' },
];

const SOURCE_COLOR = {
    winback:   '#FF007F',
    scheduled: '#3B82F6',
    template:  '#FFD700',
    manual:    '#A855F7',
};

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n) => Number(n || 0).toLocaleString();

export const PromoPerformanceTab = () => {
    const [period, setPeriod] = useState('month');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${API}/admin/reports/promo-performance?period=${period}`,
                { withCredentials: true }
            );
            setData(res.data);
        } catch {
            toast.error('Failed to load promo performance');
        }
        setLoading(false);
    };

    useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period]);

    const summary = data?.summary || {};
    const bySource = data?.by_source || [];
    const daily = data?.daily || [];

    // Chart scaling (max of given/wagered per day)
    const maxDaily = Math.max(1, ...daily.map(d => Math.max(d.given || 0, d.wagered || 0)));

    return (
        <div className="space-y-6" data-testid="promo-performance-tab">
            <div className="card-dark p-4 sm:p-6 border-[#FF007F]/20">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <div>
                        <div className="flex items-center gap-2">
                            <Gift className="w-4 h-4 text-[#FF007F]" />
                            <h3 className="text-lg font-bold text-white">Promo Performance</h3>
                        </div>
                        <p className="text-[11px] text-[#A0AAB2] mt-1 max-w-2xl">
                            Promo $ given out vs. wagering activity. Drill into which promo sources are pulling players back to the action.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={fetchData}
                        className="border-[#FF007F]/30 text-[#FF007F]" data-testid="promo-perf-refresh">
                        <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {/* Period filter */}
                <div className="flex flex-wrap gap-1 mb-5" data-testid="promo-perf-periods">
                    {PERIODS.map(p => (
                        <button
                            type="button"
                            key={p.id}
                            onClick={() => setPeriod(p.id)}
                            className={`px-3 py-1.5 rounded-lg text-[11px] font-bold border transition-colors ${
                                period === p.id
                                    ? 'bg-[#FF007F] text-white border-[#FF007F]'
                                    : 'bg-[#0a0a0a] text-white border-white/10 hover:border-[#FF007F]/40'
                            }`}
                            data-testid={`promo-perf-period-${p.id}`}>
                            {p.label}
                        </button>
                    ))}
                </div>

                {/* Summary tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-5" data-testid="promo-perf-summary">
                    <Stat icon={Gift}    label="Promo $ Given"       value={`$${fmt(summary.total_given)}`}        color="#FF007F" />
                    <Stat icon={Activity}label="Promos Sent"          value={fmtInt(summary.total_count)}           color="#FFD700" />
                    <Stat icon={Users}   label="Unique Recipients"    value={fmtInt(summary.unique_recipients)}     color="#3B82F6" />
                    <Stat icon={Gift}    label="Avg / Recipient"      value={`$${fmt(summary.avg_per_recipient)}`}  color="#A855F7" />
                    <Stat icon={TrendingUp} label="Recipients Wagered"value={`$${fmt(summary.recipients_wagered)}`} color="#39FF14" />
                    <Stat icon={Percent} label="ROI (wager / promo)"  value={`${(summary.roi_pct || 0).toFixed(0)}%`}
                        color={(summary.roi_pct || 0) >= 100 ? '#39FF14' : '#FFD700'} bold />
                </div>

                {/* Daily chart */}
                <div className="bg-[#0a0a0a] rounded-lg p-3 border border-white/5 mb-5">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-[10px] uppercase font-bold text-[#A0AAB2] tracking-wide">Daily — Promo $ vs. Wagered $</p>
                        <div className="flex items-center gap-3 text-[9px]">
                            <span className="flex items-center gap-1 text-[#FF007F]"><span className="w-2 h-2 rounded-sm bg-[#FF007F]" />Promo</span>
                            <span className="flex items-center gap-1 text-[#39FF14]"><span className="w-2 h-2 rounded-sm bg-[#39FF14]" />Wagered</span>
                        </div>
                    </div>
                    {daily.length === 0 ? (
                        <div className="text-center py-6 text-[#A0AAB2] text-xs">No promo or wager activity in this period.</div>
                    ) : (
                        <>
                            <div className="flex items-end gap-1 h-32" data-testid="promo-perf-chart">
                                {daily.map((d) => {
                                    const gh = Math.max(2, Math.round(((d.given || 0) / maxDaily) * 100));
                                    const wh = Math.max(2, Math.round(((d.wagered || 0) / maxDaily) * 100));
                                    return (
                                        <div key={d.date} className="flex-1 min-w-0 flex items-end justify-center gap-[1px] relative group cursor-default">
                                            <div className="w-1/2 bg-[#FF007F]/70 hover:bg-[#FF007F] rounded-t transition-all"
                                                style={{ height: `${gh}%` }} />
                                            <div className="w-1/2 bg-[#39FF14]/70 hover:bg-[#39FF14] rounded-t transition-all"
                                                style={{ height: `${wh}%` }} />
                                            <div className="hidden group-hover:block absolute -top-14 left-1/2 -translate-x-1/2 bg-black border border-white/20 rounded px-2 py-1 text-[10px] whitespace-nowrap z-10">
                                                <p className="text-white font-bold">{d.date}</p>
                                                <p className="text-[#FF007F] font-mono">P ${fmt(d.given)}</p>
                                                <p className="text-[#39FF14] font-mono">W ${fmt(d.wagered)}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex justify-between mt-1 text-[9px] text-[#666]">
                                <span>{daily[0]?.date}</span>
                                <span>{daily[daily.length - 1]?.date}</span>
                            </div>
                        </>
                    )}
                </div>

                {/* By Source breakdown */}
                <div data-testid="promo-perf-by-source">
                    <p className="text-[10px] uppercase font-bold text-[#A0AAB2] tracking-wide mb-2">By Source</p>
                    {bySource.length === 0 ? (
                        <div className="bg-[#0a0a0a] rounded-lg p-4 border border-white/5 text-center text-[#A0AAB2] text-xs">
                            No promos sent in this period.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-lg border border-white/5">
                            <table className="w-full text-xs">
                                <thead className="bg-[#0a0a0a] text-[#A0AAB2]">
                                    <tr>
                                        <th className="text-left p-2">Source</th>
                                        <th className="text-left p-2">Type</th>
                                        <th className="text-right p-2">Given</th>
                                        <th className="text-right p-2">Sent</th>
                                        <th className="text-right p-2">Recipients</th>
                                        <th className="text-right p-2">Avg / Recipient</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bySource.map((b, i) => {
                                        const color = SOURCE_COLOR[b.source_type] || '#FFFFFF';
                                        return (
                                            <tr key={`${b.source_type}-${b.source_name}-${i}`}
                                                className="border-t border-white/5 hover:bg-white/5"
                                                data-testid={`promo-perf-source-${i}`}>
                                                <td className="p-2 text-white font-bold">{b.source_name}</td>
                                                <td className="p-2">
                                                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase"
                                                        style={{ backgroundColor: color + '22', color }}>
                                                        {b.source_type}
                                                    </span>
                                                </td>
                                                <td className="p-2 text-right font-mono text-[#FF007F]">${fmt(b.given_total)}</td>
                                                <td className="p-2 text-right font-mono text-white">{fmtInt(b.given_count)}</td>
                                                <td className="p-2 text-right font-mono text-white">{fmtInt(b.recipients)}</td>
                                                <td className="p-2 text-right font-mono text-[#A0AAB2]">${fmt(b.avg_per_recipient)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const Stat = ({ icon: Icon, label, value, color, bold }) => (
    <div className="bg-[#0a0a0a] rounded-lg p-2.5 border border-white/5">
        <div className="flex items-center gap-1.5 mb-1">
            {Icon && <Icon className="w-3 h-3" style={{ color }} />}
            <p className="text-[9px] uppercase font-bold text-[#A0AAB2] tracking-wide truncate">{label}</p>
        </div>
        <p className={`font-mono ${bold ? 'font-black text-base' : 'font-bold text-sm'}`} style={{ color }}>{value}</p>
    </div>
);
